import React from 'react';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import styled from 'styled-components';
import { M } from '@dashboard-experience/mastodon';
import { ResidentialAddress } from 'types/report';

const FieldLabel = styled.span`
  margin-bottom: 0.5rem;
  display: block;
`;

const FirstGridCol = styled(M.GridCol)`
  padding-left: 0;
`;

interface ResidentialAddressesProps {
  addresses: ResidentialAddress[];
  lookbackYears?: string | number | null;
}

const ResidentialAddresses: React.FC<ResidentialAddressesProps> = ({
  addresses,
  lookbackYears,
}) => {
  const getAddressTitle = (address: ResidentialAddress, index: number) => {
    if (address.isCurrent) {
      return i18n.getStr(
        'components.ResidentialAddresses.currentAddressTitle',
        index + 1,
      );
    }
    return i18n.getStr(
      'components.ResidentialAddresses.addressTitle',
      index + 1,
    );
  };

  const getLookbackTitle = () => {
    if (!lookbackYears) {
      return <T value='components.ResidentialAddresses.residentialAddresses' />;
    }

    const numericValue = Number(lookbackYears);
    if (!isNaN(numericValue)) {
      return i18n.getStr(
        'components.ResidentialAddresses.residentialAddressesWithLookback',
        lookbackYears,
      );
    }

    return <T value='components.ResidentialAddresses.residentialAddresses' />;
  };

  return (
    <div className='mt-3'>
      <div className='md-flex mb-3'>
        <div>
          <div className='mb-3 font-weight-bold'>{getLookbackTitle()}</div>
        </div>

        <div>
          {addresses.map((address, index) => {
            const isLast = index === addresses.length - 1;

            return (
              <React.Fragment
                key={`${address.fromDate}-${address.fullAddress}`}
              >
                <div className='mb-3'>{getAddressTitle(address, index)}</div>

                <M.Grid>
                  <M.GridRow className={isLast ? 'mb-0' : 'mb-md-4'}>
                    <FirstGridCol className='mb-3 mb-md-0'>
                      <FieldLabel className='text-very-muted'>
                        <T value='labels.from' />
                      </FieldLabel>
                      {address.fromDate}
                    </FirstGridCol>
                    <M.GridCol className='mb-3 mb-md-0'>
                      <FieldLabel className='text-very-muted'>
                        <T value='labels.to' />
                      </FieldLabel>
                      {address.toDate || '-'}
                    </M.GridCol>
                    <M.GridCol className='mb-3 mb-md-0'>
                      <FieldLabel className='text-very-muted'>
                        <T value='labels.address' />
                      </FieldLabel>
                      {address.fullAddress?.trim() || (
                        <T value='components.ResidentialAddresses.noFixedAddress' />
                      )}
                    </M.GridCol>
                  </M.GridRow>
                </M.Grid>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResidentialAddresses;
